<template lang="pug">
study-result-list.study-result(
  ref='template',
  downloadIconImage='dummy01.png',
  :currentLink='currentLink',
  :linkSettings='linkSettings',
  :sideMenuLinkEvent='sideMenuLinkEvent',
  :logoutEvent='logoutEvent',
  :searchEvent='searchEvent',
  :gradeSelectOptions='gradeSelectOptions',
  :yearSelectOptions='yearSelectOptions',
  :monthSelectOptions='monthSelectOptions',
  :studyResultList='studyResultList',
  :resultButtonEvent='resultButtonEvent',
  :lastMinuteCheckResultLinkEvent='lastMinuteCheckResultLinkEvent',
  :resolveResultLinkEvent='resolveResultLinkEvent',
  :downloadEvent='downloadEvent',
  @yearUpdated='setMonthOptions'
)
  template(#userUidTitle) ID:
  template(#userNameSuffix) さん
  template(#logoutText) ログアウト
  template(#studyResultListTitle) 結果一覧
  template(#searchGradeLabel) 学年
  template(#searchYearLabel) 実施年度
  template(#searchMonthLabel) 実施月
  template(#searchButtonText) 検索
  template(#subjectHeader) 科目
  template(#sUnitHeader) 選択
  template(#resultHeader) テスト結果
  template(#lastMinuteCheckResultHeader) 直前チェックCBT
  template(#resolveResultHeader) 解きなおしCBT
  template(#tableResultButtonLabel) 結果を見る
  template(#downloadLabel) 帳票をダウンロードする
</template>

<script lang="ts">
import { Component, Mixins, Ref } from 'vue-property-decorator'
import LoginMethods from '@/mixins/LoginMethods'
import StudyResultList from '@/components/templates/student/StudyResultList.vue'
import { InputItem } from '@/models/inputItem'
import { StudyResultListType } from '@/models/studyResultListModel'
import { SideMenuSettings, LinkSetting } from '@/const/SideMenuSettings'
import { InputItemPlaceholderSetting } from '@/const/DisplaySettings'
import { StudyResultUtils } from '@/utils/StudyResultUtils'
import { StudySearchUtils } from '@/utils/StudySearchUtils'
import StudentStudyContentsApi from '@/mixins/api/student/StudentStudyContentsApi'
import GradesApi from '@/mixins/api/GradesApi'
import StudentStudyResultDetailApi from '@/mixins/api/student/StudentStudyResultDetailApi'
import { ApiResponseGrade } from '@/models/apiResponses/grades'
import StudyContentsApi from '@/mixins/api/StudyContentsApi'
import { ApiResponseStudyContentYear } from '@/models/apiResponses/studyContents'
import OriginalCurriculumsApi from '@/mixins/api/OriginalCurriculumsApi'

/**
 * テスト結果一覧画面
 */
@Component({
  components: {
    StudyResultList,
  },
})
export default class StudyResult extends Mixins(
  LoginMethods,
  StudentStudyContentsApi,
  GradesApi,
  StudyContentsApi,
  OriginalCurriculumsApi,
  StudentStudyResultDetailApi
) {
  // テンプレートへのアクセス
  @Ref() template!: StudyResultList

  // サイドメニューの表示設定
  private currentLink: LinkSetting = SideMenuSettings.STUDY_RESULT
  private linkSettings: LinkSetting[] = SideMenuSettings.STUDENT_SIDE_MENU

  // プルダウンのプレースホルダー設定
  private requiredPlaceholder = StudySearchUtils.buildPlaceholder(InputItemPlaceholderSetting.REQUIRED)

  // 検索プルダウン
  private gradeSelectOptions: InputItem[] = StudySearchUtils.buildGradeInputItems(this.requiredPlaceholder)
  private yearSelectOptions: InputItem[] = StudySearchUtils.buildYearInputItems(this.requiredPlaceholder)
  private monthSelectOptions: InputItem[] = StudySearchUtils.buildMonthInputItems(this.requiredPlaceholder)

  // テスト結果一覧
  private studyResultList: StudyResultListType[] | null = null

  // 初期表示自動検索実行中フラグ
  private isProcessingAutomaticSearch = false

  // 生徒画面Cookie情報
  private get studentCookie() {
    return this.$bcbtCookies.studentCookie.get() || {}
  }

  /**
   * [Vue] 画面読み込み時：初期処理
   */
  private async mounted(): Promise<void> {
    // 学年、実施年度プルダウン情報を取得
    this.$loading.start()
    const apiResponses = await Promise.all([this.loadGradeOption(), this.loadYearOption()])
    // 学年プルダウン選択肢をセット
    this.gradeSelectOptions = StudySearchUtils.buildGradeInputItems(this.requiredPlaceholder, apiResponses[0])
    // 実施年度プルダウン選択肢をセット
    this.yearSelectOptions = StudySearchUtils.buildYearInputItems(this.requiredPlaceholder, apiResponses[1])
    // Cookieに検索条件がセットされている場合は選択値に設定して自動検索
    const studyResultSearch = this.studentCookie.studyResultSearch
    if (studyResultSearch) {
      this.isProcessingAutomaticSearch = true
      this.template.gradeValue = studyResultSearch.gradeCode
      this.template.yearValue = studyResultSearch.yearValue
      this.template.monthValue = studyResultSearch.monthValue
      await this.searchEvent()
    }
    this.$loading.complete()
  }

  /**
   * 学年情報読み込み
   * @return 学年一覧
   */
  private async loadGradeOption(): Promise<ApiResponseGrade[] | null> {
    return await this.getGrades().catch(() => null)
  }

  /**
   * 実施年度読み込み
   * @return 実施年度一覧
   */
  private async loadYearOption(): Promise<ApiResponseStudyContentYear[] | null> {
    return await this.getYears().catch(() => null)
  }

  // 遷移系イベント
  private sideMenuLinkEvent(linkSetting: LinkSetting) {
    // 現ページのリンク押下時は遷移処理なし
    if (linkSetting === this.currentLink) return

    // サイドメニューリンクの該当遷移先に遷移
    this.$router.push({ name: `Student${linkSetting.name}` })
  }

  private logoutEvent() {
    this.logout()
    // ログインページへ遷移
    this.$router.push({ name: 'StudentLogin' })
  }

  // 検索
  private async searchEvent() {
    // プルダウン選択した学年、実施年度、実施月を用いて検索処理
    this.$loading.start()
    const apiResponse = await this.getStudentStudyResultContents(
      Number(this.template.yearValue),
      Number(this.template.monthValue),
      Number(this.template.gradeValue)
    )
    // studyResultListに値をセット
    this.studyResultList = StudyResultUtils.formatStudyResultList(apiResponse)
    // Cookieに検索条件をセット
    const studentCookie = this.studentCookie
    studentCookie.studyResultSearch = {
      gradeCode: this.template.gradeValue,
      yearValue: this.template.yearValue,
      monthValue: this.template.monthValue,
    }
    await this.$bcbtCookies.studentCookie.set(studentCookie)
    this.$loading.complete()
  }

  /**
   * 実施年度プルダウン切り替え時、実施月プルダウンを設定
   */
  private async setMonthOptions() {
    this.$loading.start()
    // 実施年度未選択状態の場合、プルダウンは空になる
    const apiResponse = this.template.yearValue ? await this.getMonths(Number(this.template.yearValue)) : null
    this.monthSelectOptions = StudySearchUtils.buildMonthInputItems(this.requiredPlaceholder, apiResponse)
    // 初期表示自動検索時以外は実施月を未選択状態に戻す
    if (!this.isProcessingAutomaticSearch) {
      this.template.monthValue = ''
    }
    this.isProcessingAutomaticSearch = false
    this.$loading.complete()
  }

  // 結果ボタンイベント
  private resultButtonEvent(paperTestId: number) {
    // テストIDが存在しない場合は処理をしない
    if (!paperTestId) {
      return
    }

    // テストIDを付与して詳細ページへ遷移
    this.$router.push({
      name: 'StudentStudyResultDetail',
      params: { paperTestId: String(paperTestId) },
    })
  }

  // 直前チェック結果リンクイベント
  private lastMinuteCheckResultLinkEvent(sUnitId: number) {
    // 小項目IDを付与して詳細ページへ遷移
    this.$router.push({
      name: 'StudentStudyResultLastMinuteCheck',
      params: { sUnitId: String(sUnitId) },
    })
  }

  // 解きなおし結果リンクイベント
  private resolveResultLinkEvent(sUnitId: number) {
    // 小項目IDを付与して詳細ページへ遷移
    this.$router.push({
      name: 'StudentStudyResultResolve',
      params: { sUnitId: String(sUnitId) },
    })
  }

  // ダウンロードイベント
  private async downloadEvent(originalCurriculumId: number) {
    // Cookieにセットされている検索条件のyearValueをリクエストパラメータに使用
    if (!this.studentCookie.studyResultSearch) return

    // ダウンロード処理
    this.$loading.start()
    try {
      // pdfデータを取得
      const [data] = await this.downloadStudentStudyResultPdf(
        Number(this.studentCookie.studyResultSearch.yearValue),
        originalCurriculumId
      )
      // 別タブでpdfを開く
      window.open(URL.createObjectURL(data), '_blank')
      this.$loading.complete()
    } catch {
      alert('ファイルが存在しません。')
    }
  }
}
</script>
